import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, CheckCircle2, Clock } from "lucide-react";
import { partnerWorkspaceApi } from "./partnerWorkspaceApi";
import "./partnerWorkspace.css";

// Production Partner Workspace PP2c (2026-02) — Onboarding refinement. Renders whenever the
// caller's card has no approved baseline yet, whether it's still an editable Draft or already
// submitted (Pending Approval, awaiting the founder+finance signoff) — see
// modules/partner_rate_cards.py's `_build_card_view` for the exact same condition on the
// backend (`pending_version and not approved_version`). "Decided" == priced OR not_offered;
// completion counts decided lines, NEVER just priced ones — a partner who genuinely doesn't
// supply half the catalogue must still be able to reach 100% and submit.
const GROUP_TABS = ["All", "Crew", "Equipment", "Services"];
const GROUP_TAG_STYLE = {
  Crew: { background: "var(--pw-tag-crew)", color: "#FFFFFF" },
  Equipment: { background: "var(--pw-tag-equipment)", color: "#FFFFFF" },
  Services: { background: "var(--pw-tag-services)", color: "#FFFFFF" },
};

function isDecided(item) {
  return (item.rate !== null && item.rate !== undefined) || !!item.not_offered;
}

function computeCompletion(items) {
  const byGroup = {};
  items.forEach((it) => {
    const g = it.group || "Services";
    byGroup[g] = byGroup[g] || { done: 0, total: 0 };
    byGroup[g].total += 1;
    if (isDecided(it)) byGroup[g].done += 1;
  });
  const done = Object.values(byGroup).reduce((n, g) => n + g.done, 0);
  const total = Object.values(byGroup).reduce((n, g) => n + g.total, 0);
  return { by_group: byGroup, done, total, pct: total ? Math.round((100 * done) / total) : 100 };
}

function CompletionSummary({ completion, readOnly }) {
  const groups = Object.entries(completion.by_group || {});
  return (
    <div className="pw-card p-5 mb-6" data-testid="partner-rate-card-completion-summary">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[13px] font-bold uppercase tracking-wider" style={{ color: "var(--pw-text-primary)" }}>
          {readOnly ? "Submitted for NJM review" : "Onboarding progress"}
        </span>
        <span className="pw-display text-[22px]" data-testid="partner-rate-card-completion-pct"
          style={{ color: completion.pct === 100 ? "var(--pw-success-text)" : "var(--pw-pending-text)" }}>{completion.pct}%</span>
      </div>
      <div className="h-2 rounded-full overflow-hidden mb-4" style={{ background: "var(--pw-divider)" }}>
        <div className="h-full rounded-full transition-all duration-300" data-testid="partner-rate-card-completion-bar"
          style={{ width: `${completion.pct}%`, background: completion.pct === 100 ? "var(--pw-success-dot)" : "var(--pw-yellow)" }} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {groups.map(([g, c]) => (
          <div key={g} className="rounded-xl px-3 py-2.5 flex items-center justify-between" style={{ background: "var(--pw-warm-tint)" }}
            data-testid={`partner-rate-card-completion-group-${g.toLowerCase()}`}>
            <span className="pw-pill" style={GROUP_TAG_STYLE[g] || {}}>{g}</span>
            <span className="text-[12.5px] font-bold" style={{ color: "var(--pw-text-secondary)" }}>{c.done}/{c.total} decided</span>
          </div>
        ))}
      </div>
      <p className="text-[12.5px] mt-3" style={{ color: "var(--pw-text-muted)" }}>
        {readOnly
          ? "NJM is reviewing your submitted rate card. You'll be notified once it's approved."
          : "Price every line you offer, or mark it \"Not offered\" — either counts as decided. Submit once every line is decided; you don't have to price everything."}
      </p>
    </div>
  );
}

function DraftItemRow({ item, currency, onSetItem, readOnly }) {
  const [rateInput, setRateInput] = useState(item.rate != null ? String(item.rate) : "");
  const [localNotOffered, setLocalNotOffered] = useState(!!item.not_offered);
  const [saving, setSaving] = useState(false);
  const decided = isDecided(item);

  useEffect(() => {
    setRateInput(item.rate != null ? String(item.rate) : "");
    setLocalNotOffered(!!item.not_offered);
  }, [item.rate, item.not_offered]);

  const commitRate = async () => {
    const val = parseFloat(rateInput);
    if (!rateInput.trim() || Number.isNaN(val) || val === item.rate) return;
    setSaving(true);
    try { await onSetItem(item.canonical_id, { rate: val }); }
    catch { toast.error("Couldn't save that rate."); }
    finally { setSaving(false); }
  };

  const handleToggle = async (e) => {
    const checked = e.target.checked;
    setLocalNotOffered(checked);
    if (!checked) return; // unchecking is local-only — enter a rate to un-decline (no API call without a value)
    setSaving(true);
    try { await onSetItem(item.canonical_id, { not_offered: true }); }
    catch { toast.error("Couldn't update."); setLocalNotOffered(false); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid items-center px-5" data-testid={`partner-rate-card-draft-row-${item.canonical_id}`}
      style={{ gridTemplateColumns: "2.2fr 1fr 1.2fr 1.1fr", padding: "11px 20px", borderBottom: "1px solid var(--pw-divider)" }}>
      <div className="flex items-center gap-2.5 min-w-0">
        {decided
          ? <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: "var(--pw-success-dot)" }} />
          : <span className="h-2 w-2 rounded-full shrink-0 ml-1" style={{ background: "var(--pw-text-faint)" }} />}
        <span className="text-[14px] font-semibold truncate" style={{ color: "var(--pw-text-primary)" }}>{item.service_name}</span>
      </div>
      <div className="text-[13px]" style={{ color: "var(--pw-text-muted)" }}>{item.unit || "—"}</div>
      <div className="flex items-center justify-end gap-1.5">
        {readOnly ? (
          <span className="text-[14px] font-medium">{item.not_offered ? "—" : item.rate != null ? `${currency} ${item.rate}` : "—"}</span>
        ) : (
          <>
            <span className="text-[12px]" style={{ color: "var(--pw-text-faint)" }}>{currency}</span>
            <input type="number" value={rateInput} disabled={localNotOffered || saving}
              onChange={(e) => setRateInput(e.target.value)} onBlur={commitRate}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              placeholder="Rate" data-testid={`partner-rate-card-draft-rate-input-${item.canonical_id}`}
              className="pw-input w-[96px] text-right px-2.5 py-1.5 text-[13px] disabled:opacity-50" />
          </>
        )}
      </div>
      <div className="flex items-center justify-end">
        {readOnly ? (
          item.not_offered && <span className="pw-pill" style={{ background: "var(--pw-divider)", color: "var(--pw-text-muted)" }}>Not offered</span>
        ) : (
          <label className="inline-flex items-center gap-1.5 cursor-pointer text-[12px] select-none" style={{ color: "var(--pw-text-secondary)" }}>
            <input type="checkbox" checked={localNotOffered} disabled={saving} onChange={handleToggle}
              data-testid={`partner-rate-card-draft-not-offered-${item.canonical_id}`} />
            Not offered
          </label>
        )}
      </div>
    </div>
  );
}

export default function OnboardingRateCard({ view, onReload }) {
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("All");
  const [submitting, setSubmitting] = useState(false);
  const header = view.header;
  const readOnly = header.status !== "Draft";
  const [localItems, setLocalItems] = useState(view.items);

  useEffect(() => { setLocalItems(view.items); }, [view.items]);

  const completion = useMemo(() => computeCompletion(localItems), [localItems]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = localItems.filter((it) => {
      if (groupFilter !== "All" && it.group !== groupFilter) return false;
      if (q && !(it.service_name || "").toLowerCase().includes(q)) return false;
      return true;
    });
    const byGroup = {};
    filtered.forEach((it) => { (byGroup[it.group] = byGroup[it.group] || []).push(it); });
    return byGroup;
  }, [localItems, search, groupFilter]);

  const setItem = async (canonicalId, body) => {
    const res = await partnerWorkspaceApi.setDraftItem(canonicalId, body);
    const updated = res.data.item;
    setLocalItems((prev) => prev.map((it) => (it.canonical_id === canonicalId ? { ...it, ...updated } : it)));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await partnerWorkspaceApi.submitCard();
      toast.success("Rate card submitted to NJM for approval");
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.code === "incomplete_card" ? "Some lines are still undecided." : "Couldn't submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const missing = completion.total - completion.done;

  return (
    <div data-testid="partner-rate-card-onboarding">
      <div className="mb-6" data-testid="partner-rate-card-onboarding-intro">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="pw-display text-[26px]" data-testid="partner-rate-card-title">{header.partner_name} · {readOnly ? "Rate Card Submitted" : "Set Up Your Rate Card"}</h1>
          {readOnly && (
            <span className="pw-pill inline-flex items-center gap-1" style={{ background: "var(--pw-pending-bg)", color: "var(--pw-pending-text)" }} data-testid="partner-rate-card-status-pill">
              <Clock className="h-3 w-3" /> PENDING APPROVAL
            </span>
          )}
        </div>
        <p className="text-[13.5px]" style={{ color: "var(--pw-text-secondary)" }}>
          {readOnly
            ? "Your submitted rate card is now locked while NJM's founder and finance team review it."
            : "For every line below, either enter your rate or mark it \"Not offered\". Once every line is decided you can submit for NJM's review."}
        </p>
      </div>

      <CompletionSummary completion={completion} readOnly={readOnly} />

      <div className="flex items-center gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-2 rounded-xl px-4 py-2.5 flex-1 min-w-[240px] pw-input" data-testid="partner-rate-card-onboarding-search">
          <Search className="h-3.5 w-3.5 shrink-0" style={{ color: "var(--pw-text-faint)" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search rates by item"
            className="flex-1 bg-transparent outline-none text-[14px]" style={{ color: "var(--pw-text-primary)" }} />
        </div>
        <div className="flex items-center gap-1.5" data-testid="partner-rate-card-onboarding-group-tabs">
          {GROUP_TABS.map((g) => (
            <button key={g} onClick={() => setGroupFilter(g)} data-testid={`partner-rate-card-onboarding-group-tab-${g.toLowerCase()}`}
              className={g === groupFilter ? "pw-btn-primary px-3.5 py-1.5 text-[12.5px]" : "pw-btn-secondary px-3.5 py-1.5 text-[12.5px]"}>{g}</button>
          ))}
        </div>
      </div>

      <div className="pw-card overflow-hidden mb-8" data-testid="partner-rate-card-onboarding-table">
        <div className="grid items-center px-5 py-3" style={{ gridTemplateColumns: "2.2fr 1fr 1.2fr 1.1fr", background: "#FBFAF5", borderBottom: "1px solid var(--pw-divider)" }}>
          {["Item", "Unit", "Your Rate", "Decline"].map((label, i) => (
            <div key={label} className={`text-[10.5px] font-bold uppercase tracking-wider ${i >= 2 ? "text-right" : ""}`} style={{ color: "var(--pw-text-primary)" }}>{label}</div>
          ))}
        </div>
        {GROUP_TABS.filter((g) => g !== "All" && grouped[g]?.length).map((g) => (
          <div key={g}>
            <div className="flex items-center gap-2.5 px-5 py-2.5" style={{ background: "#FCFBF7", borderBottom: "1px solid var(--pw-divider)" }}>
              <span className="pw-pill" style={GROUP_TAG_STYLE[g]}>{g}</span>
              <span className="text-[13px]" style={{ color: "var(--pw-text-muted)" }}>{grouped[g].length} item{grouped[g].length === 1 ? "" : "s"}</span>
            </div>
            {grouped[g].map((item) => (
              <DraftItemRow key={item.canonical_id} item={item} currency={header.currency} onSetItem={setItem} readOnly={readOnly} />
            ))}
          </div>
        ))}
      </div>

      {!readOnly && (
        <div className="pw-card flex items-center justify-between gap-4 flex-wrap px-5 py-4 sticky bottom-4" data-testid="partner-rate-card-onboarding-submit-bar">
          <span className="text-[13px]" style={{ color: "var(--pw-text-muted)" }} data-testid="partner-rate-card-onboarding-missing-count">
            {missing > 0 ? `${missing} line${missing === 1 ? "" : "s"} still need a rate or Not offered` : "All lines decided — ready to submit"}
          </span>
          <button onClick={handleSubmit} disabled={missing > 0 || submitting} data-testid="partner-rate-card-submit-btn"
            className="pw-btn-primary px-6 py-3 text-[14px]">
            {submitting ? "Submitting…" : "Submit Rate Card"}
          </button>
        </div>
      )}
    </div>
  );
}
